import { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import { uploadFile, mediaUrl, formatError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "w-full rounded-lg border border-slate-600 bg-[#0C131F] p-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#E8A317] focus:outline-none focus:ring-1 focus:ring-[#E8A317]";

export const ImageUpload = ({ value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file);
      onChange(res.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <div className="relative h-16 w-16 overflow-hidden rounded-lg bg-slate-800">
          <img src={mediaUrl(value)} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => onChange("")} className="absolute right-0 top-0 bg-red-600 p-0.5 text-white"><X className="h-3 w-3" /></button>
        </div>
      ) : <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-slate-600 text-slate-500"><Upload className="h-5 w-5" /></div>}
      <label className="cursor-pointer rounded-lg bg-slate-700 px-3 py-2 text-xs font-bold uppercase text-white hover:bg-slate-600">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
        <input type="file" accept="image/*" className="hidden" onChange={handle} data-testid={testid} />
      </label>
    </div>
  );
};

export const Field = ({ field, value, onChange }) => {
  const { key, label, type, options } = field;
  const tid = `field-${key}`;
  if (type === "image") return (<div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label}</label><ImageUpload value={value} onChange={onChange} testid={tid} /></div>);
  if (type === "switch") return (
    <div className="flex items-center justify-between rounded-lg border border-slate-700 p-3">
      <label className="text-sm font-semibold text-slate-200">{label}</label>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} />
    </div>
  );
  if (type === "select") return (
    <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label}</label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className={inputCls} data-testid={tid}><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  if (type === "textarea") return (<div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label}</label><textarea rows={field.rows || 3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>);
  if (type === "list") return (
    <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label} <span className="text-slate-500">(one per line)</span></label>
      <textarea rows={field.rows || 4} className={inputCls} value={Array.isArray(value) ? value.join("\n") : (value || "")} onChange={(e) => onChange(e.target.value.split("\n").filter((x) => x.trim()))} data-testid={tid} />
    </div>
  );
  return (<div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label}</label><input type={type === "number" ? "number" : "text"} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>);
};

export { inputCls };
